import { v4 as uuidv4 } from "uuid";
import React from "react";
import { SurveyQuestionApi } from "api/surveyApi";
import { Question, QuestionOptionType } from "survey";

type ActionType =
  | { type: "ADD_QUESTION"; payload: QuestionOptionType }
  | { type: "MOVE_QUESTTION_UP"; payload: number }
  | { type: "MOVE_QUESTTION_DOWN"; payload: number }
  | { type: "DELETE_QUESTION"; payload: number } // payload is the question idx
  | { type: "DUPLICATE_QUESTION"; payload: number } // payload is the question idx
  | { type: "SET_OPEN_QUESTION_IDX"; payload: number | null }
  | {
      type: "UPDATE_QUESTION";
      payload: Question;
    }
  | { type: "SET_PREVIEW_QUESTION_IDX"; payload: number }
  | { type: "INITIALIZE_SURVEY"; payload: SurveyQuestionApi[] };

type SurveyState = {
  questions: Question[];
  openQuestionIdx: number | null;
  previewQuestionIdx: number;
  triggerSubmit: boolean | null;
};

function surveyReducer(state: SurveyState, action: ActionType): SurveyState {
  switch (action.type) {
    case "ADD_QUESTION":
      const newQuestion = getEmptyQuestion(action.payload, state.questions.length);
      return { ...state, questions: [...state.questions, newQuestion], openQuestionIdx: state.questions.length };
    case "MOVE_QUESTTION_UP":
      if (action.payload === 0) return state;
      const questionToMoveUp = state.questions[action.payload];
      questionToMoveUp.index = action.payload - 1;
      const questionToMoveDown = state.questions[action.payload - 1];
      questionToMoveDown.index = action.payload;
      const newQuestions = [...state.questions];
      newQuestions[action.payload] = questionToMoveDown;
      newQuestions[action.payload - 1] = questionToMoveUp;
      return { ...state, questions: newQuestions };
    case "MOVE_QUESTTION_DOWN":
      if (action.payload === state.questions.length - 1) return state;
      const questionToMoveDown2 = state.questions[action.payload];
      questionToMoveDown2.index = action.payload + 1;
      const questionToMoveUp2 = state.questions[action.payload + 1];
      questionToMoveUp2.index = action.payload;
      const newQuestions2 = [...state.questions];
      newQuestions2[action.payload] = questionToMoveUp2;
      newQuestions2[action.payload + 1] = questionToMoveDown2;
      return { ...state, questions: newQuestions2 };
    case "DELETE_QUESTION":
      return {
        ...state,
        questions: state.questions
          .filter((question) => question.index !== action.payload)
          .map((question, idx) => ({ ...question, index: idx })),
      };
    case "DUPLICATE_QUESTION":
      const questionToDuplicate = state.questions.find((question) => question.index === action.payload);
      if (!questionToDuplicate) return state;
      const duplicatedQuestion = {
        ...questionToDuplicate,
        key: uuidv4(),
        index: state.questions.length,
        id: undefined,
      };
      return { ...state, questions: [...state.questions, duplicatedQuestion] };
    case "SET_OPEN_QUESTION_IDX":
      return { ...state, openQuestionIdx: action.payload };
    case "UPDATE_QUESTION":
      const questionIndex = state.questions.findIndex((question) => question.key === action.payload.key);
      if (questionIndex === -1) return state;
      const updatedQuestions = [...state.questions];
      updatedQuestions[questionIndex] = {
        ...updatedQuestions[questionIndex],
        question: action.payload.question,
        description: action.payload.description,
        placeholder: action.payload.placeholder,
        inputType: action.payload.inputType,
        textType: action.payload.textType,
        required: action.payload.required,
        lowerLabel: action.payload.lowerLabel,
        options: action.payload.options,
        upperLabel: action.payload.upperLabel,
        id: action.payload.id,
      };
      return { ...state, questions: updatedQuestions };
    case "SET_PREVIEW_QUESTION_IDX":
      return { ...state, previewQuestionIdx: action.payload };
    case "INITIALIZE_SURVEY":
      return {
        ...state,
        // @ts-ignore
        questions: action.payload
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((x, idx) => ({ ...x, index: idx, isValid: null, key: uuidv4() })),
      };
    default:
      return state;
  }
}

interface SurveyContextType {
  state: SurveyState;
  dispatch: React.Dispatch<ActionType>;
}

const SurveyContext = React.createContext<SurveyContextType | undefined>(undefined);

export const SurveyProvider = ({ children }: React.PropsWithChildren) => {
  const [state, dispatch] = React.useReducer(surveyReducer, {
    questions: [],
    openQuestionIdx: 0,
    previewQuestionIdx: 0,
    triggerSubmit: null,
  });

  const value = { state, dispatch };

  return <SurveyContext.Provider value={value}>{children}</SurveyContext.Provider>;
};

const getEmptySelectQuestion = (inputType: QuestionOptionType, index: number): Question => ({
  question: "Co było pierwsze?",
  description: "Wybierz jedną odpowiedź",
  inputType: inputType,
  options: [
    { value: "🥚 Jajko", id: uuidv4() },
    { value: "🐔 Kurczak", id: uuidv4() },
  ],
  index,
  key: uuidv4(),
});

const getEmptyQuestion = (type: QuestionOptionType, index: number): Question => {
  switch (type) {
    case "trainer-rating":
      return {
        question: "Oceń trenera",
        description: "",
        inputType: "trainer-rating",
        index,
        key: uuidv4(),
      };
    case "course-rating":
      return {
        question: "Oceń nasz kurs",
        description:
          "Prosimy o ocenę kursu w skali od 1 do 5 oraz zachęcamy do zostawienia opinii w polu poniżej. Dziękujemy!",
        placeholder: "Wpisz odpowiedź tutaj...",
        inputType: "course-rating",
        index,
        key: uuidv4(),
      };
    case "multi-select":
    case "single-select":
      return getEmptySelectQuestion(type, index);
    case "rating":
      return {
        question: "Oceń naszą aplikację",
        description: "Oceń w skali od 1 do 5",
        inputType: "rating",
        lowerLabel: "Niezadowolony",
        upperLabel: "Bardzo zadowolony",
        index,
        key: uuidv4(),
      };
    default:
      return getInitQuestion(index);
  }
};

const getInitQuestion = (index: number): Question => ({
  question: "?",
  description: "",
  placeholder: "Wpisz odpowiedź tutaj...",
  inputType: "free-text",
  textType: "text",
  index,
  key: uuidv4(),
});

export function useSurvey() {
  const context = React.useContext(SurveyContext);
  if (context === undefined) {
    throw new Error("useSurvey must be used within a SurveyProvider");
  }
  return context;
}
